
import React from 'react';
import { Code, BrainCircuit, LayoutDashboard, Zap, Terminal, Component } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

export type LogoVariant = 'code' | 'brain' | 'layout' | 'zap' | 'terminal' | 'component';

interface LogoProps {
  variant?: LogoVariant;
  showText?: boolean;
  className?: string;
  onClick?: (event?: React.MouseEvent) => void;
  autoFlip?: boolean;
  flipInterval?: number;
}

const Logo = ({
  variant = 'code',
  showText = true,
  className = '',
  onClick,
  autoFlip = false,
  flipInterval = 5000
}: LogoProps) => {
  const [currentVariant, setCurrentVariant] = useState<LogoVariant>(variant);
  const [isFlipping, setIsFlipping] = useState(false);
  const autoFlipTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Get all available variants
  const availableVariants: LogoVariant[] = ['code', 'brain', 'layout', 'zap', 'terminal', 'component'];

  // Update internal state when prop changes
  useEffect(() => {
    setCurrentVariant(variant);
  }, [variant]);

  // Auto-flip functionality
  useEffect(() => {
    if (autoFlip) {
      autoFlipTimerRef.current = setInterval(() => {
        triggerFlip();
      }, flipInterval);
    }

    return () => {
      if (autoFlipTimerRef.current) {
        clearInterval(autoFlipTimerRef.current);
      }
    };
  }, [autoFlip, flipInterval]);

  const triggerFlip = () => {
    // Set flipping state to trigger animation
    setIsFlipping(true);

    // After half of animation duration, change the variant
    setTimeout(() => {
      const currentIndex = availableVariants.indexOf(currentVariant);
      const nextIndex = (currentIndex + 1) % availableVariants.length;
      setCurrentVariant(availableVariants[nextIndex]);
    }, 150); // Half of the 0.3s animation duration

    // Reset flipping state after animation completes
    setTimeout(() => {
      setIsFlipping(false);
    }, 300); // Full animation duration
  };

  const renderIcon = () => {
    switch (currentVariant) {
      case 'code':
        return <Code size={24} className="text-primary" />;
      case 'brain':
        return <BrainCircuit size={24} className="text-primary" />;
      case 'layout':
        return <LayoutDashboard size={24} className="text-primary" />;
      case 'zap':
        return <Zap size={24} className="text-primary" />;
      case 'terminal':
        return <Terminal size={24} className="text-primary" />;
      case 'component':
        return <Component size={24} className="text-primary" />;
      default:
        return <Code size={24} className="text-primary" />;
    }
  };

  const handleClick = (event?: React.MouseEvent) => {
    // When clicked, trigger the flip animation and icon change
    triggerFlip();

    // If auto-flip is enabled, reset the timer
    if (autoFlip && autoFlipTimerRef.current) {
      clearInterval(autoFlipTimerRef.current);
      autoFlipTimerRef.current = setInterval(() => {
        triggerFlip();
      }, flipInterval);
    }

    // Call any passed onClick handler
    if (onClick) {
      onClick(event);
    }
  };

  return (
    <div
      className={`flex items-center gap-2 ${className}`}
      onClick={handleClick}
    >
      <div
        className={`transition-all duration-300 hover:scale-110 ${isFlipping ? 'animate-logo-flip' : ''
          }`}
      >
        {renderIcon()}
      </div>
      {showText && (
        <div className="font-mono text-base sm:text-xl font-semibold hidden sm:block">
          <span className="text-primary">&lt;</span>
          <span>AniketKasale</span>
          <span className="text-primary">/&gt;</span>
        </div>
      )}
    </div>
  );
};

export default Logo;
